import { useState, useCallback, useRef, useEffect } from 'react';
import adminFetch from '../../services/adminFetch';
import './VideoGenDebugPage.css';

const VideoGenDebugPage = () => {
  const [prompt, setPrompt] = useState('');
  const [characterImage, setCharacterImage] = useState(null);
  const [sceneImage, setSceneImage] = useState(null);
  const [charPreview, setCharPreview] = useState(null);
  const [scenePreview, setScenePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tasks, setTasks] = useState([]);
  const pollRefs = useRef({});

  useEffect(() => () => {
    Object.values(pollRefs.current).forEach(id => clearInterval(id));
  }, []);

  const startPolling = useCallback((taskId) => {
    if (pollRefs.current[taskId]) return;
    pollRefs.current[taskId] = setInterval(async () => {
      try {
        const res = await adminFetch(`/admin/debug-status/${taskId}`);
        const data = await res.json();
        if (!res.ok) return;

        setTasks(prev => prev.map(t =>
          t.taskId === taskId ? { ...t, ...data, updatedAt: new Date().toLocaleTimeString() } : t
        ));

        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRefs.current[taskId]);
          delete pollRefs.current[taskId];
        }
      } catch { /* ignore */ }
    }, 2000);
  }, []);

  const handleSubmit = async () => {
    if (!characterImage) { alert('请上传角色图片'); return; }
    if (!prompt.trim()) { alert('请输入生成描述'); return; }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('prompt', prompt.trim());
    formData.append('character_image', characterImage);
    if (sceneImage) formData.append('scene_image', sceneImage);

    const startTime = Date.now();

    try {
      const res = await adminFetch('/admin/debug-generate', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '提交失败');

      const newTask = {
        taskId: data.task_id,
        prompt: prompt.trim(),
        status: 'pending',
        progress: 0,
        message: '任务已提交...',
        startTime: new Date().toLocaleTimeString(),
        startTimestamp: startTime,
        video: null,
        error: null,
        charImageName: characterImage?.name || null,
        sceneImageName: sceneImage?.name || null
      };

      setTasks(prev => [newTask, ...prev]);
      startPolling(data.task_id);

    } catch (err) {
      alert('提交失败: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const clearCompleted = () => {
    setTasks(prev => prev.filter(t => t.status !== 'completed' && t.status !== 'failed'));
  };

  return (
    <div className="debug-page">
      <header className="debug-header">
        <h1>视频生成工具</h1>
      </header>

      <div className="debug-layout">
        {/* 左侧：参数面板 */}
        <div className="debug-panel">
          <h2>生成参数</h2>

          {/* 角色图片 */}
          <div className="debug-field">
            <label>角色图片 <span className="req">*</span></label>
            <div className="debug-file-row">
              {charPreview ? (
                <div className="debug-file-preview">
                  <img src={charPreview} alt="" />
                  <button onClick={() => { setCharacterImage(null); setCharPreview(null); }}>✕</button>
                </div>
              ) : (
                <label className="debug-file-btn">
                  + 选择角色图片
                  <input type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) { setCharacterImage(f); setCharPreview(URL.createObjectURL(f)); }
                  }} hidden />
                </label>
              )}
              {characterImage && <span className="debug-file-name">{characterImage.name}</span>}
            </div>
          </div>

          {/* 场景图片 */}
          <div className="debug-field">
            <label>场景图片 <span className="opt">(可选)</span></label>
            <div className="debug-file-row">
              {scenePreview ? (
                <div className="debug-file-preview">
                  <img src={scenePreview} alt="" />
                  <button onClick={() => { setSceneImage(null); setScenePreview(null); }}>✕</button>
                </div>
              ) : (
                <label className="debug-file-btn">
                  + 选择场景图片
                  <input type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) { setSceneImage(f); setScenePreview(URL.createObjectURL(f)); }
                  }} hidden />
                </label>
              )}
              {sceneImage && <span className="debug-file-name">{sceneImage.name}</span>}
            </div>
          </div>

          {/* Prompt */}
          <div className="debug-field">
            <label>生成描述 <span className="req">*</span></label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="描述你想生成的视频内容，例如：&#10;做一个开心挥手的动作&#10;优雅地转圈，裙摆飘起&#10;害羞地双手捂脸，然后偷看"
              rows={5}
            />
          </div>

          {/* 提交 */}
          <button
            className="debug-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !characterImage || !prompt.trim()}
          >
            {submitting ? '提交中...' : '开始生成'}
          </button>
        </div>

        {/* 右侧：结果面板 */}
        <div className="debug-results">
          <div className="debug-results-header">
            <h2>生成任务 ({tasks.length})</h2>
            {tasks.some(t => t.status === 'completed' || t.status === 'failed') && (
              <button className="debug-clear-btn" onClick={clearCompleted}>清除已完成</button>
            )}
          </div>

          {tasks.length === 0 ? (
            <div className="debug-empty">
              <p>暂无任务，提交参数后将在此显示生成进度和结果</p>
            </div>
          ) : (
            <div className="debug-task-list">
              {tasks.map(task => (
                <TaskCard key={task.taskId} task={task} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TaskCard = ({ task }) => {
  const elapsed = task.completed_at && task.startTimestamp
    ? `${((new Date(task.completed_at).getTime() - task.startTimestamp) / 1000).toFixed(1)}s`
    : task.startTimestamp
      ? `${((Date.now() - task.startTimestamp) / 1000).toFixed(0)}s`
      : '';

  return (
    <div className={`debug-task-card ${task.status}`}>
      <div className="debug-task-header">
        <span className={`debug-task-status ${task.status}`}>
          {task.status === 'completed' ? '完成' :
           task.status === 'failed' ? '失败' :
           task.status === 'generating' ? '生成中' :
           task.status === 'retrying' ? '重试中' : '等待'}
        </span>
        <span className="debug-task-time">{task.startTime} {elapsed && `(${elapsed})`}</span>
      </div>

      <div className="debug-task-prompt">{task.prompt}</div>

      {(task.charImageName || task.sceneImageName) && (
        <div className="debug-task-files">
          {task.charImageName && <span>角色: {task.charImageName}</span>}
          {task.sceneImageName && <span>场景: {task.sceneImageName}</span>}
        </div>
      )}

      <div className="debug-task-progress">
        <div className="debug-task-bar">
          <div className="debug-task-fill" style={{ width: `${task.progress}%` }} />
        </div>
        <div className="debug-task-progress-info">
          <span>{task.message}</span>
          <span>{task.progress}%</span>
        </div>
      </div>

      {task.status === 'completed' && task.video && (
        <div className="debug-task-result">
          <video
            src={task.video.url || `/static/videos/debug/${task.video.filename}`}
            controls
            muted
            preload="metadata"
            className="debug-result-video"
          />
          <div className="debug-result-meta">
            {task.video.label && (
              <span className="debug-result-tag">{task.video.emoji} {task.video.label}</span>
            )}
            {task.video.interaction_type && (
              <span className="debug-result-tag">{task.video.interaction_type}</span>
            )}
          </div>
        </div>
      )}

      {task.status === 'failed' && task.error && (
        <div className="debug-task-error">{task.error}</div>
      )}
    </div>
  );
};

export default VideoGenDebugPage;
